/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-07 13:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component, useState, useEffect } from 'react';
import {
  Modal,
  TextStyle,
  TextField,
  Scrollable,
  Button,
  Icon,
  ChoiceList,
  Select,
  Link
} from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { MobileCancelMajor, ExternalSmallMinor } from '@shopify/polaris-icons';
import { ADDONS_INFO, ANALYTICS_ADDON, DISCOUNT_ERRORS } from 'src/constants/subscription';
import {
  formatPriceDisplay,
  calculateBillingMonthly,
  getDiscountCodeValue
} from 'src/helpers/subscription-v3-helper';
import { routes } from 'src/routes';
import { exitSABFullscreen } from 'src/integrations/shopify-app-bridge/fullscreen';
import { allowToShowBFCM23, BFCM23_CODE } from 'src/helpers/dashboard-helper';
import { connect } from 'react-redux';
import { PACKAGES } from 'src/constants/subscription';
import { buildRedirectUrl, getShop } from 'src/helpers';

import { RadioButton } from '@shopify/polaris';
import { useCallback } from 'react';
import { useMemo } from 'react';

type Props = {
  active: boolean,
  handleCancel: () => void,
  handleConfirm: () => void,
  packageSelected: any
};

type ComposedProps = Props & WithI18nProps;
// DONE

const DATA_UPSELL = {
  basic_1000: '26%',
  basic_2500: '24%',
  basic_5000: '21%',
  basic_7500: '19%',
  basic_10000: '17%',
  basic_15000: '15%',
  basic_20000: '13%',
  basic_25000: '12%',
  basic_30000: '11%',
  basic_35000: '10%',
  basic_40000: '9%',
  basic_45000: '8%',
  basic_50000: '8%',
  basic_2_1000: '14%',
  basic_2_2500: '13%',
  basic_2_5000: '12%',
  basic_2_7500: '11%',
  basic_2_10000: '10%',
  basic_2_15000: '9%',
  basic_2_20000: '8%',
  basic_2_25000: '7%',
  basic_2_30000: '6%',
  basic_2_35000: '6%',
  basic_2_40000: '5%',
  basic_2_45000: '5%',
  basic_2_50000: '5%',
  essential_1000: '27%',
  essential_2500: '26%',
  essential_5000: '23%',
  essential_7500: '22%',
  essential_10000: '20%',
  essential_15000: '18%',
  essential_20000: '16%',
  essential_25000: '15%',
  essential_30000: '14%',
  essential_35000: '13%',
  essential_40000: '12%',
  essential_45000: '11%',
  essential_50000: '11%'
};

function UpsellPackage({
  packageNameFirstChoose,
  packageNumberProduct,
  addonAdded,
  analyticRange,
  i18n,
  packageUpsellName,
  setPackageUpsellName = () => {},
  ...rest
}) {
  const _setPackageUpsellName = useCallback(
    e => {
      setPackageUpsellName(
        e || packageNameFirstChoose,
        packageNameFirstChoose !== (e || packageNameFirstChoose)
      );
    },
    [setPackageUpsellName, packageNameFirstChoose]
  );

  const [isClickedUpsell, setIsClickedUpsell] = useState(false);
  const [isUpsellProfessional, setIsUpsellProfessional] = useState(false);
  const [keyUpsell, setKeyUpsell] = useState(null);

  const addonAddedName = useMemo(() => {
    return addonAdded.map(addon => addon.name);
  }, [addonAdded]);

  useEffect(() => {
    return () => {
      _setPackageUpsellName(null);
      setIsClickedUpsell(false);
    };
  }, [_setPackageUpsellName]);

  const handleChange = useCallback(
    (_, newValue) => {
      _setPackageUpsellName(newValue);
      setIsClickedUpsell(true);
    },
    [_setPackageUpsellName]
  );

  useEffect(() => {
    if (isClickedUpsell) {
      return;
    }
    if (packageNameFirstChoose === 'basic') {
      setIsUpsellProfessional(false);

      if (addonAddedName.find(addonName => addonName === 'analytics') && analyticRange === '180') {
        setIsUpsellProfessional(true);
      }

      if (addonAddedName.find(addonName => addonName === 'variantDisplay')) {
        setIsUpsellProfessional(true);
      }
    }

    if (packageNameFirstChoose === 'essential') {
      setIsUpsellProfessional(true);
    }
  }, [addonAddedName, analyticRange, packageNameFirstChoose, isClickedUpsell]);

  useEffect(() => {
    if (packageNameFirstChoose === 'basic') {
      setKeyUpsell(
        isUpsellProfessional
          ? `${packageNameFirstChoose}_2_${packageNumberProduct}`
          : `${packageNameFirstChoose}_${packageNumberProduct}`
      );
    }

    if (packageNameFirstChoose === 'essential') {
      setKeyUpsell(`${packageNameFirstChoose}_${packageNumberProduct}`);
    }
  }, [isUpsellProfessional, packageNameFirstChoose, packageNumberProduct, setKeyUpsell]);

  const upsellPercent = useMemo(() => {
    return DATA_UPSELL[keyUpsell];
  }, [keyUpsell]);

  if (packageNameFirstChoose === 'professional') {
    return <></>;
  }

  if (!isClickedUpsell && addonAddedName.length === 0) {
    return <></>;
  }

  if (addonAddedName.find(addonName => addonName === 'analytics') && analyticRange === '365') {
    return <></>;
  }

  const openComparePage = products => {
    const shop = getShop();
    const redirectUrl = buildRedirectUrl(shop, routes.subscription.comparison(products));
    window.open(redirectUrl, '_blank');
  };

  return (
    <div className="package-option">
      {packageNameFirstChoose === 'essential' ? (
        <RadioButton
          label={
            <span>
              <b>{i18n.translate('AdminHTML.SubscriptionV3.Packages.package02')}</b>
            </span>
          }
          id="essential"
          name="package"
          checked={packageUpsellName === 'essential'}
          onChange={handleChange}
        />
      ) : (
        <RadioButton
          label={
            <span>
              <b>{i18n.translate('AdminHTML.SubscriptionV3.Packages.package01')}</b>
            </span>
          }
          id="basic"
          name="package"
          checked={packageUpsellName === 'basic'}
          onChange={handleChange}
        />
      )}

      {isUpsellProfessional ? (
        <RadioButton
          label={
            <div>
              <div>
                <b>{i18n.translate('AdminHTML.SubscriptionV3.Packages.package03')}</b>
                <span
                  style={{
                    background: '#AEE9D1',
                    borderRadius: '10px',
                    marginLeft: '10px',
                    padding: '2px 8px 2px 8px'
                  }}>
                  {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.saveUpTo')}{' '}
                  {upsellPercent}
                </span>
              </div>
              <div>
                {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.saveUpToText', {
                  link: (
                    <span className="img-new-tab">
                      <Link onClick={() => openComparePage(packageNumberProduct)}>
                        {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.saveUpToLearn')}
                        <Icon source={ExternalMinor} color="interactive" />
                      </Link>
                      <span style={{ color: 'var(--p-interactive)' }}>.</span>
                    </span>
                  )
                })}
              </div>
            </div>
          }
          checked={packageUpsellName === 'professional'}
          id="professional"
          name="package"
          onChange={handleChange}
        />
      ) : (
        <RadioButton
          label={
            <div>
              <div>
                <b>{i18n.translate('AdminHTML.SubscriptionV3.Packages.package02')}</b>
                <span
                  style={{
                    background: '#AEE9D1',
                    borderRadius: '10px',
                    marginLeft: '10px',
                    padding: '2px 8px 2px 8px'
                  }}>
                  {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.saveUpTo')}{' '}
                  {upsellPercent}
                </span>
              </div>
              <div>
                {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.saveUpToText', {
                  link: (
                    <span className="img-new-tab">
                      <Link onClick={() => openComparePage(packageNumberProduct)}>
                        {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.saveUpToLearn')}
                        <Icon source={ExternalMinor} color="interactive" />
                      </Link>
                      <span style={{ color: 'var(--p-interactive)' }}>.</span>
                    </span>
                  )
                })}
              </div>
            </div>
          }
          checked={packageUpsellName === 'essential'}
          id="essential"
          name="package"
          onChange={handleChange}
        />
      )}
    </div>
  );
}

class ReviewPackageUpsellModal extends Component<ComposedProps> {
  state = {
    showDiscountCodeInput: false,
    addonList: [],
    analyticList: [],
    analytic: '90',
    addonAdded: [],
    addonsAddedName: [],
    invoice: {},
    code: '',
    preSelect: [],
    currentPackageName: _.get(this.props.packageInfo, 'name', null),
    packageUpsellInfo: null,
    viewDiscountBreakdown: false
  };

  async componentDidMount() {
    const {
      packageInfo: { addons = [] },
      invoice,
      addonsSuggestionBeta
    } = this.props;

    const addonList = [...ADDONS_INFO];
    // Build Addon list for package
    this.buildAddonList(addonList);
    // Init analytic list
    let analyticList = [...ANALYTICS_ADDON];
    const findAnalytics = addons.find(e => _.includes(e, 'analytics'));
    const splitValue = findAnalytics.split('-');
    analyticList = ANALYTICS_ADDON.filter(e => parseInt(e.value) > parseInt(splitValue[0]));

    // Init value state
    this.setState({
      addonList,
      analyticList,
      analytic: analyticList[0].value
    });

    // Init addon added
    const nextSubscribedAddOnsInfos = _.get(invoice, 'subscribedAddOnsInfos', []);
    const analyticNames = analyticList.map(e => e.name);
    await this.initAddonAdded(
      addonList,
      nextSubscribedAddOnsInfos,
      analyticNames,
      addonsSuggestionBeta
    );
  }

  componentWillReceiveProps(nextProps) {
    const { addonList, analyticList } = this.state;

    const { addonsSuggestionBeta = [], gettingInvoice } = nextProps;
    const analyticNames = analyticList.map(e => e.name);

    const nextSubscribedAddOnsInfos = _.get(nextProps.invoice, 'subscribedAddOnsInfos', []);
    if (
      this.props.gettingInvoice === true &&
      gettingInvoice === false &&
      !_.isEmpty(nextSubscribedAddOnsInfos)
    ) {
      this.initAddonAdded(
        addonList,
        nextSubscribedAddOnsInfos,
        analyticNames,
        addonsSuggestionBeta
      );
    } else if (gettingInvoice === false && addonsSuggestionBeta.length > 0) {
      const preSelect = addonList.filter(listItem => {
        return addonsSuggestionBeta.includes(listItem.addonName);
      });
      this.setState({ addonAdded: preSelect, preSelect });
    }

    this.setState({
      currentPackageName: _.get(nextProps.packageInfo, 'name', null)
    });
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    const {
      addonAdded: addonAddedPrev = [],
      analytic: analyticPrev,
      showDiscountCodeInput: showDiscountCodeInputPrev
    } = this.state;

    const { addonAdded = [], analytic, showDiscountCodeInput } = nextState;
    const { correctPlan: correctPlanPrev } = this.props;
    const { correctPlan, discount, remainCycles } = nextProps;

    if (
      (addonAdded.length !== addonAddedPrev.length ||
        analytic !== analyticPrev ||
        correctPlanPrev !== correctPlan ||
        showDiscountCodeInput !== showDiscountCodeInputPrev) &&
      correctPlan
    ) {
      const analyticValue = ANALYTICS_ADDON.find(e => e.value === analytic);
      const invoice = calculateBillingMonthly(
        correctPlan,
        discount,
        addonAdded,
        analyticValue,
        remainCycles
      );
      this.setState({ invoice });
    }
  }

  renderDetailBill = (label, list, pkgName, isAddon = false) => {
    const { i18n } = this.props;
    const { analytic, analyticList } = this.state;
    const analyticValue = ANALYTICS_ADDON.find(e => e.value === analytic);
    const options = analyticList.map(e => ({
      value: e.value,
      label: i18n.translate(e.label).toLowerCase()
    }));
    return (
      <div style={{ marginTop: '20px' }}>
        <TextStyle variation="strong">{label}</TextStyle>
        {list.map(({ name, title, price }, index) =>
          name === 'analytics' ? (
            <div key={`${name}-renderBill-${index}`}>
              <div key={`${name}-renderBill-${index}`} className="detail-bill">
                <div className="detail-bill-addon">
                  <p style={{ fontSize: '30px' }}>&#8901;</p>{' '}
                  <p>{i18n.translate(analyticValue.label)}</p>
                </div>
                <TextStyle variation="strong">${formatPriceDisplay(analyticValue.price)}</TextStyle>
              </div>
              <Select value={analytic} onChange={this.handleChangeAnalytic} options={options} />
            </div>
          ) : (
            <div key={`${name}-renderBill-${index}`} className="detail-bill">
              {isAddon ? (
                <div className="detail-bill-addon">
                  <p style={{ fontSize: '30px' }}>&#8901;</p> <p>{i18n.translate(title)}</p>
                </div>
              ) : (
                <p>{i18n.translate(title)}</p>
              )}
              <TextStyle variation="strong">${formatPriceDisplay(price)}</TextStyle>
            </div>
          )
        )}
      </div>
    );
  };

  renderAddonOption = (infos, index) => {
    const { name = '', description = '', price = 0 } = infos;
    const { i18n } = this.props;
    const { analyticList = [], analytic = '' } = this.state;
    const options = analyticList.map((e, idx) => ({
      value: e.value,
      label: i18n.translate(e.label).toLowerCase(),
      key: idx
    }));
    const analyticPrice = analyticList.find(e => e.value === analytic)?.price;
    return (
      <div key={`${name}-addonOption-${index}`} className="explore-addon-card">
        {name === 'analytics' && (
          <Select value={analytic} onChange={this.handleChangeAnalytic} options={options} />
        )}
        <div className="addon-card-price">
          {i18n.translate('AdminHTML.SubscriptionV3.Common.pricePerMonth', {
            price: name === 'analytics' ? analyticPrice : price
          })}
        </div>
        <div>{i18n.translate(description)}</div>
      </div>
    );
  };

  render() {
    const {
      showDiscountCodeInput,
      addonAdded,
      addonsAddedName,
      addonList,
      invoice,
      code,
      subscribing,
      analytic,
      currentPackageName,
      packageUpsellInfo,
      viewDiscountBreakdown
    } = this.state;
    const {
      active,
      i18n,
      verifying,
      packageInfo,
      discount: discountVerified = {},
      correctPlan = {},
      allowApplyDiscountCode,
      remainCycles,
      msgVerifyError,
      tenantApp,
      packageSelected
    } = this.props;
    const { title, name } = packageUpsellInfo || packageInfo;
    const { price = 0 } = correctPlan;
    const {
      total = price,
      originTotal = price,
      discountAmount = 0,
      expiredDate = '',
      discountDetail = []
    } = invoice;

    const bfcm23 = allowToShowBFCM23(tenantApp);
    const isChosenAddOnAnalytic365 = addonAdded
      ?.find(addon => addon.addonName === 'analytics')
      ?.value?.includes(365);
    const isChosenPackageProfessional = packageInfo.name === 'professional';
    const conditionBfcm23Trial = bfcm23.allow && bfcm23.type === 'trial';
    const conditionBfcm23Upgrade =
      bfcm23.allow && bfcm23.type === 'upgrade' && bfcm23.packagesHighlight.includes(name);
    const conditionBfcm23 = conditionBfcm23Trial || conditionBfcm23Upgrade;
    const conditionBfcm23Analytic =
      bfcm23.allow &&
      bfcm23.type === 'professional365' &&
      isChosenAddOnAnalytic365 &&
      isChosenPackageProfessional;

    const packageUpsellName = packageUpsellInfo?.name || currentPackageName;

    const discountType = _.get(discountVerified, 'discountType', 'percentage');
    const { dcRawValue, dcValue, dcPostfixValue } = getDiscountCodeValue(discountVerified);

    return (
      <Modal large open={active} onClose={this.handleCancel}>
        <div className="review-package-modal">
          <div className="review-package">
            <Scrollable style={{ maxHeight: '624px' }}>
              <div>{i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.title')}</div>
              <div>
                {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.subTitle', {
                  t1: (
                    <span className="link" onClick={() => this.goToSubscriptionPage()}>
                      {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.subTitle_t1')}
                      <Icon source={ExternalSmallMinor} />
                    </span>
                  )
                })}
              </div>
              <div>
                <strong>
                  {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.labelAddon')}
                </strong>
              </div>
              {/* Choice list addon */}
              <ChoiceList
                allowMultiple
                choices={addonList?.map((addon, index) => {
                  return {
                    label: i18n.translate(addon.title),
                    value: addon.name, // value is object like {...} will return warning lib
                    renderChildren: () => this.renderAddonOption(addon, index)
                  };
                })}
                selected={addonsAddedName}
                onChange={value => {
                  const addonAdded = addonList.filter(ao => value.includes(ao.name));
                  this.setState({ addonAdded: addonAdded, addonsAddedName: value });
                  // Re-Verify when update list addon
                  if (
                    !_.isEmpty(discountVerified) &&
                    _.get(discountVerified, 'scope', 'global') === 'add-on' &&
                    !value.length
                  ) {
                    this.clearDiscountCode();
                  }
                }}
              />
              <p style={{ marginTop: '20px' }}>
                {packageUpsellName === 'essential' &&
                  i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.noteEssential')}
                {packageUpsellName === 'professional' &&
                  i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.noteProfessional')}
              </p>
            </Scrollable>
          </div>
          <div className="package-billing">
            <div className="billing-title">
              <span style={{ fontSize: '20px' }}>
                {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.yourOrder')}
              </span>
              <Button onClick={this.handleCancel} plain icon={MobileCancelMajor} />
            </div>
            {conditionBfcm23 && (
              <div className="billing-title-code-bfcm23">
                {i18n.translate('AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Package_apply', {
                  code: <b>{BFCM23_CODE.PLAN}</b>
                })}
              </div>
            )}
            {conditionBfcm23Analytic && (
              <div className="billing-title-code-bfcm23">
                {i18n.translate(
                  'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Package_apply_analytic',
                  {
                    code: <b>{BFCM23_CODE.ANALYTIC365}</b>
                  }
                )}
              </div>
            )}
            {/* Package info +  addon added */}
            <div className="package-info">
              <UpsellPackage
                packageNameFirstChoose={packageInfo.name}
                packageNumberProduct={packageSelected}
                addonAdded={addonAdded}
                analyticRange={analytic}
                i18n={i18n}
                packageUpsellName={packageUpsellName}
                setPackageUpsellName={this.setPackageUpsellName}
              />
              {this.renderDetailBill(
                i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.package'),
                [{ title, price }],
                name,
                false
              )}
              {addonAdded.length > 0 &&
                this.renderDetailBill(
                  i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.addon'),
                  addonAdded,
                  name,
                  true
                )}
            </div>
            {/* ---------------- */}
            {/* Discount code info */}
            <div className="discount-code-info">
              {code && dcValue ? (
                <div className="discount-applied">
                  <p>
                    {i18n.translate(
                      'AdminHTML.SubscriptionV3.ReviewPackage.textFieldDiscount_Label'
                    )}
                  </p>
                  <div
                    className={`code ${(conditionBfcm23 || conditionBfcm23Analytic) && 'bfcm23'}`}>
                    <div>
                      <p>{code}</p>
                      <Button plain icon={MobileCancelMajor} onClick={this.clearDiscountCode} />
                    </div>
                    <TextStyle variation="strong">-${formatPriceDisplay(discountAmount)}</TextStyle>
                  </div>
                  <div>
                    {remainCycles &&
                      remainCycles > 0 &&
                      i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.discountPreview', {
                        priceDiscount: <strong>{formatPriceDisplay(discountAmount)}</strong>,
                        duration: <strong>{remainCycles}</strong>,
                        totalInvoice: <strong>{formatPriceDisplay(total)}</strong>,
                        date: <strong>{expiredDate}</strong>,
                        originalPrice: <strong>{originTotal}</strong>
                      })}
                    {!viewDiscountBreakdown && discountType === 'percentage' && (
                      <div
                        style={{ color: '#2C6ECB', cursor: 'pointer' }}
                        onClick={() => {
                          this.setState({ viewDiscountBreakdown: true });
                        }}>
                        {i18n.translate('AdminHTML.SubscriptionV3.PlanStatus.seeDiscountBreakDown')}
                      </div>
                    )}
                    {viewDiscountBreakdown && discountType === 'percentage' && (
                      <>
                        <div>
                          {remainCycles > 0
                            ? i18n.translate(
                                'AdminHTML.SubscriptionV3.PlanStatus.discountInfo_Label',
                                { value: dcPostfixValue, duration: remainCycles }
                              )
                            : i18n.translate(
                                'AdminHTML.SubscriptionV3.PlanStatus.discountInfo_Label_Lifetime',
                                { value: dcPostfixValue }
                              )}
                        </div>
                        {discountDetail && (
                          <ul style={{ marginLeft: '-20px' }}>
                            {discountDetail.map(item => {
                              return (
                                <li key={item.name}>
                                  {i18n.translate(item.name)}
                                  <span className="discount-breakdown">
                                    -${formatPriceDisplay(item.price)}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <p>
                      {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.termAndCondition', {
                        t1: (
                          <span
                            onClick={this.handledTermNCondition}
                            style={{ color: '#2C6ECB', cursor: 'pointer' }}>
                            {i18n.translate(
                              'AdminHTML.SubscriptionV3.ReviewPackage.termAndCondition_t1'
                            )}
                          </span>
                        )
                      })}
                    </p>
                  </div>
                </div>
              ) : showDiscountCodeInput ? (
                <div>
                  <div className="discountCode">
                    <TextField
                      value={code || ''}
                      label={i18n.translate(
                        'AdminHTML.SubscriptionV3.ReviewPackage.textFieldDiscount_Label'
                      )}
                      onChange={this.handleChangeCode}
                      placeholder={i18n.translate(
                        'AdminHTML.SubscriptionV3.Common.discountCodePlaceHolder'
                      )}
                      connectedRight={
                        <Button
                          primary
                          onClick={this.verify}
                          loading={verifying}
                          disabled={code ? false : true}>
                          {i18n.translate('AdminHTML.SubscriptionV3.Actions.apply')}
                        </Button>
                      }
                    />
                  </div>
                  {/* Code is not empty or percent > 0*/}
                  {showDiscountCodeInput && msgVerifyError && code && !dcRawValue && (
                    <div className={'applied error'}>
                      {DISCOUNT_ERRORS[msgVerifyError]
                        ? i18n.translate(DISCOUNT_ERRORS[msgVerifyError])
                        : msgVerifyError}
                    </div>
                  )}
                </div>
              ) : (
                allowApplyDiscountCode && (
                  <p
                    onClick={() => {
                      this.toggleModal('showDiscountCodeInput');
                    }}>
                    {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.addDiscount')}
                  </p>
                )
              )}
            </div>
            {/* ----------end---------- */}
            <div className="divider" />
            <div className="total">
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SubscriptionV3.Common.total')}
              </TextStyle>
              <TextStyle variation="strong">${formatPriceDisplay(total)}</TextStyle>
            </div>
            <Button loading={subscribing} primary onClick={this.handleConfirm}>
              {i18n.translate('AdminHTML.SubscriptionV3.ReviewPackage.confirmAction')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Handle init addon added
  initAddonAdded = (
    addonList,
    nextSubscribedAddOnsInfos,
    analyticNames,
    addonsSuggestionBeta = [],
    { packageUpsellName, isClickUpsell } = {}
  ) => {
    const preSelect = addonList.filter(listItem => {
      const matchingObj = nextSubscribedAddOnsInfos.find(subscribeAddon => {
        if (subscribeAddon.addOnName && subscribeAddon.addOnName.includes('analytics'))
          return (
            listItem.addonName === 'analytics' &&
            !subscribeAddon.cancelAt &&
            analyticNames.includes(subscribeAddon.addOnName)
          );
        return subscribeAddon.addOnName === listItem.addonName && !subscribeAddon.cancelAt;
      });
      return matchingObj !== undefined || addonsSuggestionBeta.includes(listItem.addonName);
    });

    const analyticsObj = nextSubscribedAddOnsInfos.find(
      subscribeAddon =>
        subscribeAddon.addOnName.includes('analytics') &&
        !subscribeAddon.cancelAt &&
        analyticNames.includes(subscribeAddon.addOnName)
    );
    if (analyticsObj) {
      const analyticsDays = parseInt(analyticsObj.addOnName.split('-')[1]);
      if (!isNaN(Number(analyticsDays))) {
        this.setState({ analytic: String(analyticsDays) });
      }
    }
    if (isClickUpsell && packageUpsellName === 'essential') {
      this.setState({
        addonAdded: [],
        addonsAddedName: [],
        preSelect,
        addonList
      });
    } else if (isClickUpsell && packageUpsellName === 'professional') {
      this.setState({
        analytic: '365',
        addonAdded: [],
        addonsAddedName: [],
        preSelect,
        addonList
      });
    } else {
      this.setState({
        addonAdded: preSelect,
        addonsAddedName: preSelect.map(ao => ao.name),
        preSelect,
        addonList
      });
    }
  };
  // Change discount code
  handleChangeCode = code => {
    this.setState({ code });
  };

  // Clear discount code
  clearDiscountCode = () => {
    const { clearDiscountCode } = this.props;
    // this.handleChangeCode('');
    this.setState({ showDiscountCodeInput: false, code: null, viewDiscountBreakdown: false });

    clearDiscountCode();
  };
  // Verify discount code
  verify = async () => {
    const { addonAdded, analytic, code } = this.state;
    const { verifyDiscountCode, correctPlan } = this.props;

    const analyticValue = ANALYTICS_ADDON.find(e => e.value === analytic);
    const addOns = addonAdded.map(addon => {
      if (addon.addonName === 'analytics') {
        return `${addon.addonName}-${analytic}`;
      }
      return addon.addonName;
    });
    const newPlanName = correctPlan?.name;
    await verifyDiscountCode(code, 'subscription-page', addOns, newPlanName).then(result => {
      if (result.type === 'VERIFY_DISCOUNT_CODE_SUCCESS') {
        const discountCode = _.get(result, 'payload.data.discount');
        const remainCycles = _.get(result, 'payload.data.remainCycles');
        const invoice = calculateBillingMonthly(
          correctPlan,
          discountCode,
          addonAdded,
          analyticValue,
          remainCycles
        );
        this.setState({ invoice });
      }
    });
  };

  // Close modal
  handleCancel = () => {
    this.clearDiscountCode();
    const { preSelect = [] } = this.state;
    // Clear all state
    this.setState({
      addonAdded: preSelect,
      addonsAddedName: preSelect.map(ao => ao.name)
    });
    this.props.handleCancel();
  };

  handledTermNCondition = value => {
    this.props.handledTermNCondition();
  };
  // Handle subscribe
  handleConfirm = () => {
    const { addonAdded = [], analytic = '90', code } = this.state;
    const { discount } = this.props;
    const { dcRawValue } = getDiscountCodeValue(discount);

    const addonSubscribe = addonAdded.map(addon => {
      const { addonName = '' } = addon;
      if (_.includes(addonName, 'analytics')) {
        const analyticSelect = ANALYTICS_ADDON.find(e => e.value === analytic);
        return _.get(analyticSelect, 'name', null);
      }
      return addonName;
    });

    this.props.handleConfirm(addonSubscribe, dcRawValue > 0 ? code : null);
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  setPackageUpsellName = (packageUpsellName, isClickUpsell) => {
    this.setState({ addonAdded: [] });
    this.setState({ currentPackageName: packageUpsellName });
    const packageUpsellInfo = PACKAGES.find(_package => packageUpsellName === _package.name);

    const {
      packageInfo: { addons = [] },
      invoice,
      addonsSuggestionBeta,
      changeCorrectPlan,
      packageSelected
    } = this.props;

    changeCorrectPlan(packageUpsellName, +packageSelected, () => {
      const analyticDay = {
        basic: '90',
        essential: '180',
        professional: '365'
      };

      this.setState(
        {
          packageUpsellInfo,
          analytic: analyticDay[packageUpsellName]
        },
        () => {
          const addonList = [...ADDONS_INFO];

          // Build Addon list for package
          this.buildAddonList(addonList);
          // Init analytic list
          let analyticList = [...ANALYTICS_ADDON];
          const findAnalytics = addons.find(e => _.includes(e, 'analytics'));
          const splitValue = findAnalytics.split('-');
          analyticList = ANALYTICS_ADDON.filter(e => parseInt(e.value) > parseInt(splitValue[0]));
          // Init addon added
          const nextSubscribedAddOnsInfos = _.get(invoice, 'subscribedAddOnsInfos', []);
          const analyticNames = analyticList.map(e => e.name);

          this.initAddonAdded(
            addonList,
            nextSubscribedAddOnsInfos,
            analyticNames,
            addonsSuggestionBeta,
            { packageUpsellName, isClickUpsell }
          );
        }
      );
    });
  };

  // Build addon list exclude add on in package
  buildAddonList = addonList => {
    const { packageInfo, useFreeVariant } = this.props;
    const { packageUpsellInfo } = this.state;
    const { addons = [] } = packageUpsellInfo || packageInfo;

    if (!addons || addons.length <= 0) return;
    addons.forEach(item => {
      _.remove(addonList, option => {
        return item === option.name;
      });
    });

    if (useFreeVariant) {
      _.remove(addonList, option => {
        return option.addonName === 'variant-display';
      });
    }
  };

  // Handle change analytic add on
  handleChangeAnalytic = value => {
    this.setState({ analytic: value });
  };

  goToSubscriptionPage = () => {
    const { history } = this.props;
    history.push(routes.subscription.plan());
    exitSABFullscreen();
  };
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  return {
    tenantApp
  };
};

export default withI18n()(connect(mapStateToProps, null)(ReviewPackageUpsellModal));
